import { unwrap } from '@mrigl/api-client';
import { Alert, Button, TextField } from '@mrigl/ui';
import { useState, type SubmitEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../api.js';
import { AuthLayout, consumeReturnTo } from '../components.js';
import { useI18n } from '../i18n.js';
import { useSession } from '../session.js';
import { useSurface } from '../surface.js';

type Step =
  | { readonly kind: 'email' }
  | { readonly kind: 'code'; readonly email: string; readonly challengeId: string };

/** Passwordless sign-in: email → 6-digit code → MFA. */
export function SignInPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const surface = useSurface();
  const brand =
    surface === 'console' ? `${t('brand.name')} · ${t('admin.nav.operator')}` : t('brand.name');
  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function requestCode(address: string) {
    setBusy(true);
    setError(null);
    try {
      const { challengeId } = await unwrap(
        api.POST('/admin-api/v1/auth/email/start', { body: { email: address, locale } }),
      );
      setStep({ kind: 'code', email: address, challengeId });
      setCode('');
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  async function verify(event: SubmitEvent) {
    event.preventDefault();
    if (step.kind !== 'code') return;
    setBusy(true);
    setError(null);
    try {
      const result = await unwrap(
        api.POST('/admin-api/v1/auth/email/verify', {
          body: { challengeId: step.challengeId, email: step.email, code: code.trim() },
        }),
      );
      await refresh();
      // Accounts with two-step verification enter their authenticator code next.
      void navigate(result.nextStep === 'done' ? consumeReturnTo() : '/mfa', { replace: true });
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  if (step.kind === 'email') {
    return (
      <AuthLayout>
        <h1 className="mrigl-heading">{t('admin.signIn.title', { brand })}</h1>
        <p className="mrigl-muted">{t('admin.signIn.intro')}</p>
        {error === null ? null : <Alert tone="error">{error}</Alert>}
        <form
          className="mrigl-stack"
          onSubmit={(event) => {
            event.preventDefault();
            void requestCode(email.trim());
          }}
        >
          <TextField
            label={t('admin.signIn.email')}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            required
            ltr
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <Button type="submit" busy={busy}>
            {t('admin.signIn.sendCode')}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="mrigl-heading">{t('admin.signIn.codeTitle')}</h1>
      <p className="mrigl-muted">
        {t('admin.signIn.codeIntro', { email: '⁨' + step.email + '⁩' })}
      </p>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <form className="mrigl-stack" onSubmit={(event) => void verify(event)}>
        <TextField
          label={t('admin.signIn.code')}
          name="code"
          autoComplete="one-time-code"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          ltr
          className="mrigl-code"
          value={code}
          onChange={(event) => {
            setCode(event.target.value.replace(/\D/g, ''));
          }}
        />
        <Button type="submit" busy={busy}>
          {t('admin.signIn.continue')}
        </Button>
      </form>
      <div className="mrigl-row">
        <Button variant="link" disabled={busy} onClick={() => void requestCode(step.email)}>
          {t('admin.signIn.resend')}
        </Button>
        <Button
          variant="link"
          onClick={() => {
            setStep({ kind: 'email' });
            setError(null);
          }}
        >
          {t('admin.signIn.changeEmail')}
        </Button>
      </div>
    </AuthLayout>
  );
}
